import { PageContainer, ProLayout } from '@ant-design/pro-components';
import { LogoutOutlined } from '@ant-design/icons';
import { useLocalStorageState, useRequest } from 'ahooks';
import { App, Breadcrumb, ConfigProvider, Dropdown, Tabs, message, Space } from 'antd';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import store from 'store2';
import routes from '@/router/routes';
import { expandAliasPermissions, markedRoutes } from '@/utils';

/**
 * APIs
 */
import { logout, me } from '@/services/auth';

/**
 * Components
 */
import ComponentLanguage from '@/components/language';
import { useLayoutStore } from '@/store/layout';
import antdEnUS from 'antd/locale/en_US';
import antdZhCN from 'antd/locale/zh_CN';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
import intlEnUS from '@/locales/en-US';
import intlZhCN from '@/locales/zh-CN';

/**
 * Types
 */
import type { MenuDataItem, ProLayoutProps } from '@ant-design/pro-components';
import type { ConfigProviderProps, DropdownProps, TabsProps } from 'antd';
/** 布局用账号信息：登录 /v1/me 写入 localStorage 的 account */
type LayoutAccount = {
    id: string;
    account?: string;
    name?: string;
    username?: string;
};

const getAccountDisplayName = (account?: LayoutAccount | null) =>
    account?.account ?? account?.name ?? account?.username ?? '';

/**
 * Constants
 */
const localeMaps: Record<string, any> = {
    'en-US': {
        antd: antdEnUS,
        dayjs: 'en',
        intl: intlEnUS,
    },
    'zh-CN': {
        antd: antdZhCN,
        dayjs: 'zh-cn',
        intl: intlZhCN,
    },
};

const routesWithoutLayout = ['/login', '/403'];

type RouteItem = (MenuDataItem & RouteObject) & {
    children?: RouteItem[];
    unaccessible?: boolean;
    [key: string]: any;
};

type TabItem = {
    key: string;
    tab: string;
    closable: boolean;
};

const cloneRoute = (route: RouteItem): RouteItem => {
    const cloned = { ...route } as RouteItem;
    if (route.children) {
        cloned.children = route.children.map((child) => cloneRoute(child));
    } else {
        delete cloned.children;
    }
    return cloned;
};

const cloneRoutes = (list: RouteItem[]): RouteItem[] => list.map((route) => cloneRoute(route));

const LayoutIndex: React.FC = () => {
    /**
     * Hooks
     */
    const location = useLocation();
    const navigate = useNavigate();

    /**
     * States
     */
    const { config, layout, locale } = useLayoutStore();

    const [permissions, setPermissions] = useLocalStorageState<string[]>('permissions');

    const [account, setAccount] = useLocalStorageState<LayoutAccount>('account', {
        defaultValue: {
            id: '',
            account: '',
        },
    });

    /**
     * Requests
     */
    useRequest(me, {
        ready: !!account?.id,
        onSuccess: ({ data: { code, data, msg } }) => {
            if (code !== 200) {
                return message.error(msg);
            }

            const { permissions, roles, ...accountData } = data;

            const nextAccount = { ...accountData, roles } as LayoutAccount;

            setPermissions(expandAliasPermissions(permissions));
            setAccount(nextAccount);
            store({ permissions, roles, account: nextAccount });
        },
    });

    /**
     * ChildrenProps
     */
    const configProps: ConfigProviderProps = {
        locale: localeMaps[locale!].antd,
        ...config,
    };

    const dropdownProps: DropdownProps = {
        menu: {
            items: [
                {
                    key: 'signOut',
                    label: (
                        <Space align="center">
                            <LogoutOutlined />
                            <FormattedMessage id="signOut" />
                        </Space>
                    ),
                },
            ],
            onClick: ({ key }) => {
                if (key === 'signOut') {
                    logout()
                        .then(({ data: { code, msg } }) => {
                            if (code !== 200) {
                                message.error(msg);
                                return;
                            }

                            store.clear();

                            window.location.href = '/login';
                        })
                        .catch((e) => {
                            message.error(get(e, 'response.data.msg', 'Error'));
                        });
                }
            },
        },
        placement: 'bottomRight',
    };

    const processedRoutes = useMemo<RouteItem[]>(() => {
        const copiedRoutes = cloneRoutes(routes as unknown as RouteItem[]);
        copiedRoutes.forEach((route, index) => {
            copiedRoutes[index] = markedRoutes(route as unknown as MenuDataItem) as RouteItem;
        });
        return copiedRoutes;
    }, [permissions]);

    const intlMessages = useMemo(() => {
        return (localeMaps[locale!].intl || {}) as Record<string, string>;
    }, [locale]);

    const isRouteWithoutLayout = routesWithoutLayout.includes(location.pathname);

    const getRouteLabel = useCallback(
        (route?: RouteItem | null) => {
            if (!route) return '';
            const { name, path } = route;
            if (isValidElement(name)) {
                const props = name.props as { id?: string };
                if (props.id) {
                    return intlMessages[props.id] || props.id;
                }
            }
            if (typeof name === 'string') {
                return name;
            }
            return path ? String(path) : '';
        },
        [intlMessages],
    );

    const getChainForPath = useCallback(
        (pathname: string): RouteItem[] => {
            const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
            const chain: RouteItem[] = [];
            let currentRoutes = processedRoutes;

            segments.forEach((segment) => {
                const match = currentRoutes.find((route) => {
                    const routePath = route.path || '';
                    return routePath === segment || routePath === `/${segment}`;
                }) as RouteItem | undefined;
                if (match && !match.unaccessible) {
                    chain.push(match);
                    currentRoutes = (match.children as RouteItem[]) || [];
                } else {
                    currentRoutes = [];
                }
            });

            const rootRoute = processedRoutes.find((route) => route.path === '/' || route.path === '') as
                | RouteItem
                | undefined;
            if (rootRoute && !rootRoute.unaccessible) {
                return rootRoute.path === '/' || rootRoute.path === '' ? [rootRoute, ...chain] : [rootRoute, ...chain];
            }

            return chain;
        },
        [processedRoutes],
    );

    const [tabItems, setTabItems] = useState<TabItem[]>([]);
    const [activeTabKey, setActiveTabKey] = useState<string>(location.pathname || '/');

    const currentRouteChain = useMemo(() => getChainForPath(location.pathname), [getChainForPath, location.pathname]);

    const getTargetRoute = useCallback(
        (path: string): RouteItem | undefined => {
            const chain = getChainForPath(path);
            return chain[chain.length - 1];
        },
        [getChainForPath],
    );

    const shouldShowTab = useCallback(
        (path: string) => {
            if (!path || path === '/' || path === '') {
                return false;
            }
            if (routesWithoutLayout.includes(path)) {
                return false;
            }
            const targetRoute = getTargetRoute(path);
            if (!targetRoute || targetRoute.unaccessible) {
                return false;
            }
            if (!targetRoute.element) {
                return false;
            }
            return true;
        },
        [getTargetRoute],
    );

    const breadcrumbItems = useMemo(() => {
        if (isRouteWithoutLayout) {
            return [] as { title: string; path: string }[];
        }
        if (!location.pathname || location.pathname === '/') {
            return [];
        }
        const items: { title: string; path: string }[] = [];
        const segments = location.pathname.replace(/^\//, '').split('/').filter(Boolean);

        currentRouteChain.forEach((route, index) => {
            const title = getRouteLabel(route) || route.path || '/';
            if (index === 0 && (route.path === '/' || route.path === '')) {
                items.push({ title, path: '/' });
                return;
            }

            if (index === 0 && route.path && route.path !== '/') {
                const path = route.path.startsWith('/') ? route.path : `/${route.path}`;
                items.push({ title, path });
                return;
            }

            if (index > 0) {
                const partialSegments = segments.slice(0, index);
                const path = partialSegments.length ? `/${partialSegments.join('/')}` : '/';
                items.push({ title, path });
            }
        });

        if (!items.length) {
            const homeRoute = processedRoutes.find((route) => route.path === '/' || route.path === '') as
                | RouteItem
                | undefined;
            if (homeRoute && !homeRoute.unaccessible) {
                items.push({ title: getRouteLabel(homeRoute) || 'Home', path: '/' });
            }
        }

        return items;
    }, [currentRouteChain, getRouteLabel, isRouteWithoutLayout, location.pathname, processedRoutes]);

    useEffect(() => {
        const key = location.pathname || '/';
        if (!shouldShowTab(key)) {
            setActiveTabKey('');
            if (key === '/' || key === '') {
                setTabItems([]);
            }
            return;
        }

        const targetRoute = getTargetRoute(key);
        if (!targetRoute || targetRoute.unaccessible) {
            return;
        }

        const label = getRouteLabel(targetRoute) || targetRoute.path || key;

        setTabItems((prev) => {
            const exists = prev.some((tab) => tab.key === key);
            if (exists) {
                return prev.map((tab) => (tab.key === key ? { ...tab, tab: label } : tab));
            }
            const closable = key !== '/';
            return [...prev.filter((tab) => tab.key !== key), { key, tab: label, closable }];
        });
        setActiveTabKey(key);
    }, [getRouteLabel, getTargetRoute, location.pathname, shouldShowTab]);

    useEffect(() => {
        setTabItems((prev) =>
            prev
                .filter((tab) => shouldShowTab(tab.key))
                .map((tab) => {
                    const targetRoute = getTargetRoute(tab.key);
                    const updatedLabel = getRouteLabel(targetRoute) || tab.tab;
                    return { ...tab, tab: updatedLabel };
                }),
        );
    }, [getRouteLabel, getTargetRoute, shouldShowTab]);

    useEffect(() => {
        setActiveTabKey((prev) => {
            if (!prev) {
                return prev;
            }
            if (tabItems.some((tab) => tab.key === prev)) {
                return prev;
            }
            return tabItems[tabItems.length - 1]?.key || '';
        });
    }, [tabItems]);

    const handleTabChange = useCallback(
        (key: string) => {
            setActiveTabKey(key);
            if (key !== location.pathname) {
                navigate(key);
            }
        },
        [location.pathname, navigate],
    );

    const handleTabRemove = useCallback(
        (targetKey: string) => {
            if (targetKey === '/') {
                return;
            }

            setTabItems((prevTabs) => {
                const filtered = prevTabs.filter((tab) => tab.key !== targetKey);
                if (filtered.length === prevTabs.length) {
                    return prevTabs;
                }

                if (!filtered.length) {
                    setActiveTabKey('');
                    navigate('/');
                    return filtered;
                }

                if (targetKey === activeTabKey) {
                    const nextActive = filtered[filtered.length - 1];
                    setActiveTabKey(nextActive.key);
                    navigate(nextActive.key);
                }

                return filtered;
            });
        },
        [activeTabKey, navigate],
    );

    const tabItemsForRender: TabsProps['items'] = tabItems.map((item) => ({
        key: item.key,
        label: item.tab,
        closable: item.closable,
    }));

    const resolvedActiveTabKey = useMemo(() => {
        if (tabItems.some((tab) => tab.key === activeTabKey)) {
            return activeTabKey;
        }
        return tabItems[tabItems.length - 1]?.key;
    }, [activeTabKey, tabItems]);

    const accountDisplayName = getAccountDisplayName(account);

    const layoutProps: ProLayoutProps = {
        avatarProps: {
            children: (accountDisplayName || '?').charAt(0).toUpperCase(),
            size: 'small',
            title: accountDisplayName || undefined,
            render: (_, dom) => <Dropdown {...dropdownProps}>{dom}</Dropdown>,
        },
        contentStyle: {
            padding: 24,
        },
        layout: 'mix',
        location,
        logo: '/favicon.svg',
        pure: isRouteWithoutLayout,
        route: {
            routes: processedRoutes,
        },
        siderWidth: 240,
        title: 'Socrates Admin',
        splitMenus: true,
        actionsRender: () => [<ComponentLanguage key="language" />],
        menuFooterRender: (props) => `v${props?.collapsed ? __VERSION__.match(/^(\d+\.\d+)/)?.[0] : __VERSION__}`,
        menuItemRender: (item, dom) => <Link to={item.path!}>{dom}</Link>,
        ...layout,
    };

    /**
     * Effects
     */
    dayjs.locale(localeMaps[locale!].dayjs);

    useEffect(() => {
        const pathname = location.pathname;
        const lastSegment = pathname.split('/').pop() || '/';
        if (lastSegment === 'login' || lastSegment === '403') {
            return;
        }

        const routeChain = getChainForPath(pathname);
        const targetRoute = routeChain[routeChain.length - 1];

        if (!targetRoute || targetRoute.unaccessible) {
            window.location.replace('/403');
            return;
        }

        if (targetRoute.children?.length && !targetRoute.element) {
            const firstAccessibleChild = (targetRoute.children as RouteItem[]).find((child) => !child.unaccessible);
            if (firstAccessibleChild?.path) {
                const basePath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
                const childPath = firstAccessibleChild.path.startsWith('/')
                    ? firstAccessibleChild.path
                    : `${basePath}/${firstAccessibleChild.path}`.replace(/\/+/g, '/');
                if (childPath !== pathname) {
                    navigate(childPath, { replace: true });
                }
            }
        }
    }, [getChainForPath, location.pathname, navigate]);

    const breadcrumbRenderItems = useMemo(
        () =>
            breadcrumbItems.map((item, index) => {
                const isLast = index === breadcrumbItems.length - 1;
                return {
                    title: isLast ? item.title : <Link to={item.path}>{item.title}</Link>,
                };
            }),
        [breadcrumbItems],
    );

    return (
        <ConfigProvider {...configProps}>
            <App>
                <IntlProvider locale={locale!} messages={localeMaps[locale!].intl}>
                    <ProLayout {...layoutProps}>
                        <PageContainer
                            header={{
                                title: undefined,
                                breadcrumb: undefined,
                                style: {
                                    paddingInline: 0,
                                    paddingBlock: 0,
                                },
                            }}
                            token={{
                                paddingInlinePageContainerContent: 0,
                                paddingBlockPageContainerContent: 0,
                            }}
                        >
                            {!isRouteWithoutLayout && breadcrumbRenderItems.length ? (
                                <Breadcrumb items={breadcrumbRenderItems} style={{ marginBottom: 16 }} />
                            ) : null}
                            {!isRouteWithoutLayout && tabItemsForRender.length > 0 ? (
                                <Tabs
                                    type="editable-card"
                                    hideAdd
                                    items={tabItemsForRender}
                                    activeKey={resolvedActiveTabKey}
                                    onChange={handleTabChange}
                                    onEdit={(key, action) => {
                                        if (action === 'remove') {
                                            handleTabRemove(key as string);
                                        }
                                    }}
                                    style={{ marginBottom: 16 }}
                                />
                            ) : null}
                            <Outlet />
                        </PageContainer>
                    </ProLayout>
                </IntlProvider>
            </App>
        </ConfigProvider>
    );
};

export default LayoutIndex;
